import { Link, createRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { LIMIARES_SAUDE, ROTAS } from '@remoto/shared';
import { rotaPainel } from './painel';
import { chamar } from '../api';
import { Cartao, Selo } from '../componentes/layout';
import { Tela } from '../workspace/Tela';
import { tempoRelativo } from '../formato';
import { IDADE_MAXIMA_DA_COTA_MIN } from '@remoto/protocolo';

/**
 * A cota de um motor em uma frase — ou a ausência dela, dita por extenso.
 *
 * O corte é o MESMO do `sugerirMotor` (`IDADE_MAXIMA_DA_COTA_MIN`): duas
 * respostas para "esta leitura ainda vale?" fariam a tela dizer 42% enquanto o
 * formulário de criar diz "sem leitura recente", e quem visse as duas não
 * saberia em qual acreditar.
 */
function cotaEmTexto(cota: { janelas?: { utilizacao: number }[]; em?: string } | null): string {
  const lida = cota?.em ? Date.parse(cota.em) : Number.NaN;
  const velha =
    Number.isNaN(lida) || Date.now() - lida > IDADE_MAXIMA_DA_COTA_MIN * 60_000;
  const valores = (cota?.janelas ?? []).map((j) => j.utilizacao);
  if (velha || valores.length === 0) return 'cota sem leitura recente';
  return `cota em ${Math.round(Math.max(...valores) * 100)}%`;
}

/**
 * O retrato do cerebro (F14) que esta maquina hospeda, do pulso dela.
 *
 * `null` e "esta maquina nao tem cerebro configurado" — nao aparece linha
 * nenhuma. `ok:false` e "tem, e nao responde": e a informacao que os
 * `alertas` ja badge de amarelo em cima, aqui e so o detalhe por maquina.
 */
interface CerebroNaSaude {
  ok: boolean;
  modelo: string | null;
  pedacos: number | null;
  ultimaIngestaoEm: string | null;
  ingestaoOk: boolean | null;
  erro: string | null;
}

/**
 * O retrato do Codex (F15) daquela máquina, do pulso dela.
 *
 * Os três estados são os mesmos do cérebro, e pela mesma razão: `null` é "esta
 * máquina não tem Codex configurado" — e não desenha linha nenhuma —, `ok:false`
 * é "tem, e não responde", e `ok:true` é o normal.
 */
interface CodexNaSaude {
  ok: boolean;
  versao: string | null;
  processos: number | null;
  threads: number | null;
  erro: string | null;
}

/** A cota de um motor: a janela mais cheia é a que manda. */
interface CotaNaSaude {
  janelas?: { utilizacao: number }[];
  em?: string;
}

interface MaquinaNaSaude {
  id: string;
  nome: string;
  papel: string;
  estado: string;
  ultimoPulsoEm: string | null;
  memoriaLivreMb: number | null;
  versaoAgente: string | null;
  loginMestreOk: boolean | null;
  loginExpiraEm: string | null;
  cerebro: CerebroNaSaude | null;
  codex: CodexNaSaude | null;
  cotaCodex: CotaNaSaude | null;
}

interface Saude {
  banco: boolean;
  discoPct: number | null;
  ultimoBackupEm: string | null;
  gravacoesEmCurso: number;
  fila: Record<string, number>;
  maquinas: MaquinaNaSaude[];
  alertas: { codigo: string; mensagem: string }[];
}


/**
 * O atalho para os avisos.
 *
 * O cartão inteiro mudou-se para `/notificacoes`, que qualquer papel enxerga.
 * Ele morava aqui, numa página só de administrador — e foi por isso que um
 * aparelho de quem não administra nunca teve como se inscrever.
 */
function AtalhoDeAvisos(): React.JSX.Element {
  return (
    <Cartao titulo="Avisos">
      <p className="text-sm text-zinc-400">
        Ligar os avisos deste aparelho, ver quais estão inscritos e escolher o que interrompe você
        agora fica em{' '}
        <Link to="/notificacoes" className="text-sky-400 hover:underline">
          Avisos
        </Link>
        .
      </p>
    </Cartao>
  );
}

function SaudeDoSistema(): React.JSX.Element {
  const { data } = useQuery({
    queryKey: ['admin-saude'],
    queryFn: () => chamar<Saude>(ROTAS.admin.saude),
    refetchInterval: 30_000,
  });

  return (
    <Tela>
      <AtalhoDeAvisos />

      {data && data.alertas.length > 0 ? (
        <div className="space-y-2">
          {data.alertas.map((a) => (
            <p
              key={a.codigo + a.mensagem}
              className="rounded-md border border-amber-900 bg-amber-950/40 px-4 py-2 text-sm text-amber-200"
            >
              {a.mensagem}
            </p>
          ))}
        </div>
      ) : null}

      <Cartao titulo="Servidor">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-xs text-zinc-500">Banco</dt>
            <dd className={data?.banco ? 'text-emerald-300' : 'text-rose-300'}>
              {data?.banco ? 'respondendo' : 'fora do ar'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-zinc-500">Disco</dt>
            <dd className={(data?.discoPct ?? 0) >= LIMIARES_SAUDE.discoCheioPct ? 'text-amber-300' : 'text-zinc-200'}>
              {data?.discoPct === null || data?.discoPct === undefined
                ? '—'
                : data.discoPct + '% usado'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-zinc-500">Último backup</dt>
            <dd className="text-zinc-200">{tempoRelativo(data?.ultimoBackupEm)}</dd>
          </div>
          <div>
            <dt className="text-xs text-zinc-500">Gravações em curso</dt>
            <dd className="text-zinc-200">{data?.gravacoesEmCurso ?? 0}</dd>
          </div>
        </dl>
        {data && Object.keys(data.fila).length > 0 ? (
          <p className="mt-4 text-xs text-zinc-500">
            Fila:{' '}
            {Object.entries(data.fila)
              .map(([estado, n]) => `${n} ${estado.toLowerCase()}`)
              .join(' · ')}
          </p>
        ) : null}
      </Cartao>

      <Cartao titulo="Máquinas">
        <ul className="divide-y divide-zinc-800">
          {(data?.maquinas ?? []).map((m) => (
            <li key={m.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div>
                <p className="flex items-center gap-2 font-medium text-zinc-100">
                  {m.nome} <Selo ligada={m.estado === 'ONLINE'} />
                </p>
                <p className="mt-0.5 text-xs text-zinc-500">
                  {m.papel.toLowerCase()} · pulso {tempoRelativo(m.ultimoPulsoEm)}
                  {m.memoriaLivreMb ? ` · ${m.memoriaLivreMb} MB livres` : ''}
                  {m.versaoAgente ? ` · agente ${m.versaoAgente}` : ''}
                </p>
                {m.cerebro ? (
                  <p
                    className={
                      'mt-0.5 text-xs ' +
                      (m.cerebro.ok === false
                        ? 'text-rose-300'
                        : m.cerebro.ingestaoOk === false
                          ? 'text-amber-300'
                          : 'text-zinc-500')
                    }
                  >
                    cérebro:{' '}
                    {m.cerebro.ok === false
                      ? `fora do ar — ${m.cerebro.erro ?? 'sem motivo'}`
                      : `${m.cerebro.pedacos ?? 0} pedaços · ingerido ${tempoRelativo(m.cerebro.ultimaIngestaoEm)}` +
                        (m.cerebro.modelo ? ` · ${m.cerebro.modelo}` : '')}
                  </p>
                ) : null}
                {/*
                  O Codex, com a MESMA regra do cérebro: `null` não desenha
                  linha. Uma máquina que não tem Codex não é uma máquina com
                  problema, e uma linha cinza dizendo "sem Codex" em toda
                  máquina da frota é ruído que ensina a não ler a coluna.
                */}
                {m.codex ? (
                  <p
                    data-teste="linha-do-codex"
                    className={'mt-0.5 text-xs ' + (m.codex.ok === false ? 'text-rose-300' : 'text-zinc-500')}
                  >
                    codex:{' '}
                    {m.codex.ok === false
                      ? `fora do ar — ${m.codex.erro ?? 'sem motivo'}`
                      : `${m.codex.processos ?? 0} processo(s) · ${m.codex.threads ?? 0} thread(s)` +
                        (m.codex.versao ? ` · ${m.codex.versao}` : '')}
                    {' · '}
                    {/*
                      "sem leitura recente" e nunca 0%: cota velha não é cota
                      zerada, e mostrar zero convidaria a escolher o motor que
                      ninguém sabe como está.
                    */}
                    {cotaEmTexto(m.cotaCodex)}
                  </p>
                ) : null}
              </div>
              {m.loginMestreOk === false ? (
                <span className="text-xs text-amber-300">login do Claude expirado</span>
              ) : m.loginExpiraEm ? (
                <span className="text-xs text-zinc-500">
                  login até {new Date(m.loginExpiraEm).toLocaleDateString('pt-BR')}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      </Cartao>
    </Tela>
  );
}

export const rotaSaude = createRoute({
  getParentRoute: () => rotaPainel,
  path: '/admin/saude',
  component: SaudeDoSistema,
});
